import React, { useState, useMemo, useEffect, useCallback, HTMLProps } from 'react';
import moment from "moment";
import { useListCustomersTopLevel, useListCustomerProfiles, AcceptedCommodity, acceptedCommodityTypes, RiskCropProfile, RiskCropProfileHistory, RiskManagementCustomerProfile } from '../../lib/api/risk/profiles';
import { custom, z } from 'zod';
import { Decimal } from 'decimal.js';
import {
  CashSalesFiltersValidation, RiskManagementCashSales, useListRiskCashSales, useListStagedCashSales,
  useProcessStagedRow, useDeleteStagedRow
} from '../../lib/api/risk/cashSales';
import { CommonDestinationTimezone, createDateLabel, GenerateLoadingOrError, genFlatOptionList, getUniqueStrings } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '../otc/useDebounce';
import { useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { Badge } from "@/components/ui/badge";
import { X, ChevronDown, ChevronUp, DeleteIcon, Trash2Icon, ArrowDownIcon, ArrowRightIcon } from "lucide-react";
import { DataTable } from "@/components/ui/virtualizedTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, Search, Plus, Edit } from "lucide-react";
import { CreateCropProfileDialog, EditCropProfileDialog } from './CropProfileForm';
import { useListCustomers } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { getOptionPredictedExpirationDate, formatCommodityToUom, formatNumberToDisplay, safeParseDecimal, sortOptionMonths } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Commodities, CommodityOptions, Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SummaryCard from './SummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RiskManagementSummaryTables from './SummaryTables';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ContractType, generateContractTypesWith } from '@/lib/api/generalContract';
import { UploadCashReportDialog } from './UploadCashReportDialog';
import { DatePicker } from '@/components/ui/typeable_date_picker';
import { useToast } from "@/components/ui/use-toast";
import { Label } from '@/components/ui/label';
import { BinaryCropTypeSelection, useListRiskSummary, RiskManagementSummaryResult, useListRiskSummaryFullRows, BinaryCropTypeSelectionOptions } from '@/lib/api/risk/summary';
import { createArrayOfSize } from '@/lib/freightReport/frieghtReportFunctions';
import { ContractTypeOptions, FlatPutOrCallOptions, ContractType as OtcContractType, PutOrCallOptions } from '@/lib/api/otc';
import { nonActionContractColumns } from '@/section/otc/Dashboard';
import { RiskCashSummaryCommodityLevel } from './CashSummaryCommodityLevel';

type ExpandedState = Record<string, boolean>;

interface TableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  contractId: string;
  customerName: string;
  hta: Decimal | null;
  unpxdBasis: Decimal | null;
}

interface OtcTableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customerId: string;
  customerName: string;
  otcId: string;
  otcContract: any;
}

interface CommoditySummaryRow {
  id: string;
  commodity: string;
  commodityId: string;
  customer_id: string;
  customer_name: string;
  projected_production: string | Decimal;
  hta_volume: string | Decimal;
  basis_volume: string | Decimal;
  beacon_volume: string | Decimal;
  beacon_quantity: string | Decimal;
  rjo_percent_hedged: string | Decimal;
  without_beacon_percent_hedged: string | Decimal;
  beacon_percent_hedged: string | Decimal;
  rjo_options: string;
  otc_contracts: Record<string, Decimal | string>;
}

interface ClientPositionTableRow {
  id: string;
  commodity: string;
  commodityId: string;
  customer_id: string;
  customer_name: string;

  clientPositionId: string;
  clientPosition: {
    contract: string;
    d_period: string;
    exp_date: string;
    put_call_type: string;
    strike: string;
    net: string;
    buy: string;
    sell: string;
    avg_price: string;
  }
}

type FiltersState = z.infer<typeof CashSalesFiltersValidation>;

interface SearchFilterState {
  searchTerm: string;
  showFilters: boolean;
}

type TabType = "summary" | "corn" | "soybeans" | "canola" | "wheat";


// NOTE: These arent actually general they are cash sales
export function GeneralContractSection({ data }: { data: RiskManagementSummaryResult | undefined; tradersList: Trader[] }) {
  // Process data into rows
  const { all_rows, grouped_keys, hasRowValues } = useMemo(() => {
    if (!data?.summary) {
      return { rowGroups: {}, grouped_keys: [], hasRowValues: false };
    }

    let hasRowValues = false;
    const allRows: Record<string, TableRow[]> = {};
    const { commodity: commodityData, commodity_customer_map } = data.summary;

    // Iterate through each commodity
    Object.entries(commodityData || {}).forEach(([commodityId, commoditySummary]) => {
      // Iterate through each customer in this commodity
      const customerMap = commodity_customer_map[commodityId] || {};

      Object.entries(commoditySummary || {}).forEach(([customerId, customerSummary]) => {
        if (!hasRowValues) { hasRowValues = true; }

        const customerName = customerMap[customerId] || 'Unknown Customer';
        const qtyMap = customerSummary.general_contract_types_quantities || {};
        const commodityDisplay = CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;

        if (!allRows[commodityDisplay]) {
          allRows[commodityDisplay] = [];
        }

        if (qtyMap["HTA"] || qtyMap["Basis"]) {
          allRows[commodityDisplay].push({
            id: `${commodityId}_:_${customerId}`,
            commodity: commodityDisplay,
            commodityId,
            customerId,
            customerName,
            contractId: `${commodityId}_:_${customerId}`,
            hta: qtyMap["HTA"] ? new Decimal(qtyMap["HTA"]) : null,
            unpxdBasis: qtyMap["Basis"] ? new Decimal(qtyMap["Basis"]) : null,
          });
        }
      });
    });

    Object.entries(allRows).forEach(([_key, rows]: any) => {
      rows.sort((a: TableRow, b: TableRow) => {
        const commodityCompare = a.commodity.localeCompare(b.commodity);
        if (commodityCompare !== 0) return commodityCompare;
        const customerCompare = a.customerName.localeCompare(b.customerName);
        if (customerCompare !== 0) { return customerCompare; }
        return a.contractId.localeCompare(b.contractId);
      })
    })

    const grouped_keys = getUniqueStrings(Object.keys(allRows).filter(item => allRows[item]?.length > 0)).sort();

    // Sort by commodity, then by customer name
    return {
      grouped_keys,
      all_rows: grouped_keys?.flatMap(item => allRows[item]) || [],
      hasRowValues
    };
  }, [data]);

  const columns: any[] = [
    {
      accessorKey: 'commodity',
      size: 60,
      header: ({ table }: any) => (
        <>
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
          </button>{' '}
          Commodity
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            // Since rows are flattened by default,
            // we can use the row.depth property
            // and paddingLeft to visually indicate the depth
            // of the row
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          <div>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: 'pointer' },
                }}
              >
                {row.getIsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
              </button>
            ) : (null)}{' '}
            {getValue()}
          </div>
        </div>
      ),
      footer: (props: any) => props.column.id,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'customerName',
      header: 'Customer',
      width: 300,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'hta',
      header: 'HTA',
      width: 150,
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.hta, { emptyFormat: "--", }); },
      aggregatedCell: ({ getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", }) },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'unpxdBasis',
      header: "Unpx'd Basis",
      width: 150,
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => {
        return formatNumberToDisplay(row.original.unpxdBasis, { emptyFormat: "--", });
      },
      aggregatedCell: ({ getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", }) },
      enableColumnFilter: false,
    }
  ];

  return (
    <div className="space-y-2 p-2">
      <div className="">
        <div className="flex justify-between py-2">
          <h2 className="text-xl font-semibold mb-4">Cash Sales</h2>
        </div>
        <div className="mx-4">
          <DataTable

            defaultGrouping={["commodity"]}
            height="600px"
            columns={columns}
            data={all_rows!}
            className={``}
          />
        </div>
      </div>
    </div>
  );
}

export function OtcContractSection({ data }: { data: RiskManagementSummaryResult | undefined; tradersList: Trader[] }) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const {
    otc_ids, closed_otc_ids
  } = useMemo(() => {
    return {
      otc_ids: getUniqueStrings(Object.values(data?.summary.commodity || {}).flatMap(customerCommoditySummaryMap => {
        return Object.values(customerCommoditySummaryMap || {}).flatMap(item => item.otc_contract_ids);
      }) || []),
      closed_otc_ids: getUniqueStrings(Object.values(data?.summary.commodity || {}).flatMap(customerCommoditySummaryMap => {
        return Object.values(customerCommoditySummaryMap || {}).flatMap(item => item.closed_otc_contract_ids);
      }) || []),
    };
  }, [data || {}])

  const otcListResult = useListRiskSummaryFullRows({
    filters: { otc_ids, closed_otc_ids },
  })

  const { otcContracts, closedOtcContracts } = otcListResult?.data?.data || { otcContracts: [], closedOtcContracts: [] };

  const { all_rows, grouped_keys, hasRowValues } = useMemo(() => {
    if (!data?.summary) {
      return { rowGroups: {}, grouped_keys: [], hasRowValues: false };
    }

    let hasRowValues = false;
    const allRows: Record<string, OtcTableRow[]> = {};
    const { commodity: commodityData, commodity_customer_map } = data.summary;

    // Iterate through each commodity
    Object.entries(commodityData || {}).forEach(([commodityId, commoditySummary]) => {
      // Iterate through each customer in this commodity
      const customerMap = commodity_customer_map[commodityId] || {};

      Object.entries(commoditySummary).forEach(([customerId, customerSummary]) => {
        if (!hasRowValues) { hasRowValues = true; }

        const customerName = customerMap[customerId] || 'Unknown Customer';
        const otcIds = customerSummary.otc_contract_ids || [];
        const closedOtcIds = customerSummary.closed_otc_contract_ids || [];
        const commodityDisplay = CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;

        if (!allRows[commodityDisplay]) {
          allRows[commodityDisplay] = [];
        }
        for (const otcId of otcIds) {
          const found = otcContracts?.find((contract: any) => contract.id === otcId);
          if (!found) { continue; }

          allRows[commodityDisplay].push({
            id: `${commodityId}_:_${customerId}`,
            commodity: commodityDisplay,
            commodityId,
            customerId,
            customerName,
            otcId,
            otcContract: found
          });
        }

        for (const closedOtcId of closedOtcIds) {
          const found = closedOtcContracts?.find((contract: any) => contract.id === closedOtcId);
          if (!found) { continue; }
          allRows[commodityDisplay].push({
            id: `${commodityId}_:_${customerId}`,
            commodity: commodityDisplay,
            commodityId,
            customerId,
            customerName,
            otcId: closedOtcId,
            otcContract: found
          });
        }
      });
    });

    Object.entries(allRows).forEach(([_key, rows]: any) => {
      rows.sort((a: OtcTableRow, b: OtcTableRow) => {
        const commodityCompare = a.commodity.localeCompare(b.commodity);
        if (commodityCompare !== 0) { return commodityCompare; }
        const customerCompare = a.customerName.localeCompare(b.customerName);
        if (customerCompare !== 0) { return customerCompare; }
        return a.otcId.localeCompare(b.otcId);
      })
    })

    const grouped_keys = getUniqueStrings(Object.keys(allRows).filter(item => allRows[item]?.length > 0)).sort();
    // Sort by commodity, then by customer name
    return {
      grouped_keys,
      all_rows: grouped_keys?.flatMap(item => allRows[item]) || [],
      hasRowValues
    };
  }, [data, otcContracts]);

  const loadingOrErrorUi = GenerateLoadingOrError([otcListResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const columns: any[] = [
    {
      accessorKey: 'commodity',
      size: 150,
      header: ({ table }: any) => (
        <>
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
          </button>{' '}
          Commodity
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            // Since rows are flattened by default,
            // we can use the row.depth property
            // and paddingLeft to visually indicate the depth
            // of the row
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          <div>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: 'pointer' },
                }}
              >
                {row.getIsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
              </button>
            ) : (null)}{' '}
            {getValue()}
          </div>
        </div>
      ),
      footer: (props: any) => props.column.id,
    },
    {
      key: 'customerName',
      name: 'Customer',
      renderCell: ({ row }: any) => { return row.customerName; },
      enableCustomerFilter: true,
    },
    {
      key: 'status',
      name: 'Status',
      renderCell: ({ row }: any) => { return row.otcContract.status === "open" ? "Open" : "Closed"; }
    },
    {
      key: 'date',
      name: 'Date',
      renderCell: ({ row }: any) => {
        return moment.tz(row.otcContract.date, CommonDestinationTimezone).format("MM/DD/YYYY");
      },
      accessorFn: (row: any) => moment.tz(row.otcContract.date, CommonDestinationTimezone).toDate(),
      aggregatedCell: () => "",
      sortingFn: "datetime",
      filterFn: "dateTimeFilter",
      enableColumnFilter: true
    },
    {
      key: 'open',
      name: 'Open',
      renderCell: ({ row }: any) => { return "Open"; }
    },
    {
      key: 'brokerage_hta',
      name: 'Brokerage HTA',
      renderCell: ({ row }: any) => { return row.otcContract.AccountNoA?.account_id || ""; }
    },
    {
      key: 'brokerage_otc',
      name: 'Brokerage OTC',
      renderCell: ({ row }: any) => { return row.otcContract.AccountNoB?.account_id; }
    },
    {
      key: 'contract_no',
      name: 'Contract #',
      renderCell: ({ row }: any) => { return row.otcContract.contract_no; }
    },
    {
      key: 'contract_type',
      name: 'Type',
      className: "min-w-[200px]",
      renderCell: ({ row }: any) => { return ContractTypeOptions.find(item => item.value === row.otcContract.contract_type)?.label || row.otcContract.contract_type; }
    },
    {
      key: 'comments',
      name: 'Comments',
      className: "max-w-[200px] min-w-[200px]",
      renderCell: ({ row }: any) => { return ""; }
    },
    {
      key: 'quantity',
      name: 'Quantity',
      renderCell: ({ row }: any) => { return formatNumberToDisplay(row.otcContract.quantity, { emptyFormat: "--", }); }
    },
    {
      key: 'otc_option_month',
      name: 'OTC Opt Month',
      renderCell: ({ row }: any) => { return row.otcContract?.option_month || ""; }
    },
    {
      key: 'start_date',
      name: 'Start Date',
      renderCell: ({ row }: any) => { return createDateLabel(row.otcContract.start_date); }
    },
    {
      key: 'expiration_date',
      name: 'Expiration Date',
      renderCell: ({ row }: any) => { return createDateLabel(row.otcContract.end_date); }
    },
    {
      key: 'weeks',
      name: 'Weeks',
      renderCell: ({ row }: any) => { return row.otcContract.weeks; }
    },
    {
      key: 'period_qty',
      name: 'Period Qty',
      renderCell: ({ row }: any) => { return row.otcContract.period_qty; }
    },
    {
      key: 'knockout',
      name: 'Knockout',
      renderCell: ({ row }: any) => { return row.otcContract.knockout; }
    },
    {
      key: 'step_one',
      name: 'Step One',
      renderCell: ({ row }: any) => { return row.otcContract.step_one; }
    },
    {
      key: 'step_two',
      name: 'Step Two',
      renderCell: ({ row }: any) => { return row.otcContract.step_two; }
    },
    {
      key: 'step_three',
      name: 'Step Three',
      renderCell: ({ row }: any) => { return row.otcContract.step_three; }
    },
    {
      key: 'double_up',
      name: 'Double Up',
      renderCell: ({ row }: any) => { return row.otcContract.double_up; }
    },
    {
      key: 'option_premium',
      name: 'Option Premium',
      renderCell: ({ row }: any) => { return row.otcContract.option_premium; }
    },
    {
      key: 'contract_fee',
      name: 'Initial Premium + Fee',
      renderCell: ({ row }: any) => { return row.otcContract.contract_fee; }
    },
    {
      key: 'margin',
      name: 'Lighthouse Fee',
      renderCell: ({ row }: any) => { return row.otcContract.margin; }
    },
    {
      key: 'margin_amount',
      name: 'Margin Amount',
      renderCell: ({ row }: any) => { return row.otcContract.margin_amount; }
    },
    {
      key: 'trade_reference',
      name: 'Trade Reference',
      renderCell: ({ row }: any) => { return row.otcContract.trade_reference; }
    },
    {
      key: 'futures_price',
      name: 'Futures Price',
      renderCell: ({ row }: any) => { return row.otcContract.futures_price; }
    },
    {
      key: 'otc_futures',
      name: 'Otc Futures',
      renderCell: ({ row }: any) => { return row.otcContract.otc_futures; }
    },
    {
      key: 'rolled_option_month',
      name: 'Futures Rolled Month',
      renderCell: ({ row }: any) => { return row.otcContract.rolled_option_month; }
    },
    {
      key: 'rolled_basis_month',
      name: 'Basis Rolled Month',
      renderCell: ({ row }: any) => { return row.otcContract.rolled_basis_month; }
    },
    {
      key: 'carry',
      name: 'Futures Carry',
      renderCell: ({ row }: any) => { return formatNumberToDisplay(row.otcContract.futures_carry, { emptyFormat: "--", }); }
    },
    {
      key: 'basis_carry',
      name: 'Basis Carry',
      renderCell: ({ row }: any) => { return formatNumberToDisplay(row.otcContract.basis_carry, { emptyFormat: "--", }); }
    },
    {
      key: 'adj_option_premium',
      name: 'Adj Option Premium',
      className: "min-w-[200px]",
      renderCell: ({ row }: any) => { return row.otcContract.adj_option_premium; }
    },
    {
      key: 'adj_contract_fee',
      name: 'Adj Initial Premium + Fee',
      className: "min-w-[200px]",
      renderCell: ({ row }: any) => { return row.otcContract.adj_contract_fee; }
    },
    {
      key: 'adj_margin',
      name: 'Adj Lighthouse Fee',
      renderCell: ({ row }: any) => { return formatNumberToDisplay(row.otcContract.adj_margin, { emptyFormat: "--", }); }
    },
    {
      key: 'adj_margin_amount',
      name: 'Adj Margin Amount',
      renderCell: ({ row }: any) => { return formatNumberToDisplay(row.otcContract.adj_margin_amount, { emptyFormat: "--", }); }
    },
    {
      key: 'last_adjusted_date',
      name: 'Last Adj Date',
      renderCell: ({ row }: any) => { return createDateLabel(row.otcContract.last_adjusted_date); }
    },
  ].map(item => ({ ...item, enableColumnFilter: item.enableColumnFilter || false, accessorKey: item.accessorKey || item.key, header: item.header || item.name, cell: item.cell || (item.renderCell ? ({ row }: any) => item.renderCell({ row: row.original }) : undefined) }));

  const toggleExpanded = (commodity: string) => {
    setExpanded(prev => ({
      ...prev,
      [commodity]: !prev[commodity]
    }));
  };

  return (
    <div className="space-y-2 p-2">
      <div className="">
        <div className="flex justify-between py-2">
          <h2 className="text-xl font-semibold mb-4">OTC</h2>
        </div>
        <div className="overflow-x-auto">
          <DataTable
            defaultSorting={[{ id: "date", desc: true }]}
            defaultGrouping={["commodity"]}
            height="600px"
            columns={columns}
            data={all_rows!}
            className={``}
          />
        </div>
      </div>
    </div>
  );
}

export function RjoSection({ data }: { data: RiskManagementSummaryResult | undefined; tradersList: Trader[] }) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const client_position_ids = useMemo(() => {
    return getUniqueStrings(Object.values(data?.summary.commodity || {}).flatMap(customerCommoditySummaryMap => {
      return Object.values(customerCommoditySummaryMap || {}).flatMap(item => item.client_position_ids);
    }) || []);
  }, [data || {}])

  const clientPositionListResult = useListRiskSummaryFullRows({
    filters: { client_position_ids },
  })

  const { clientPositions } = clientPositionListResult?.data?.data || { clientPositions: [] };

  const { all_rows, grouped_keys, hasRowValues } = useMemo(() => {
    if (!data?.summary) {
      return { rowGroups: {}, grouped_keys: [], hasRowValues: false };
    }

    let hasRowValues = false;
    const allRows: Record<string, ClientPositionTableRow[]> = {};
    const { commodity: commodityData, commodity_customer_map } = data.summary;

    // Iterate through each commodity
    Object.entries(commodityData || {}).forEach(([commodityId, commoditySummary]) => {
      // Iterate through each customer in this commodity
      const customerMap = commodity_customer_map[commodityId] || {};

      Object.entries(commoditySummary).forEach(([customerId, customerSummary]) => {
        if (!hasRowValues) { hasRowValues = true; }

        const customerName = customerMap[customerId] || 'Unknown Customer';
        const clientPositionIds = customerSummary.client_position_ids || [];
        const commodityDisplay = CommodityOptions.find(option => option.value === commodityId)?.label || commodityId;

        if (!allRows[commodityDisplay]) {
          allRows[commodityDisplay] = [];
        }
        for (const clientPositionId of clientPositionIds) {
          const clientPosition = clientPositions?.find((contract: any) => contract.id === clientPositionId);
          if (!clientPosition) { continue; }

          allRows[commodityDisplay].push({
            id: `${commodityId}_:_${customerId}`,
            commodity: commodityDisplay,
            commodityId,
            customer_id: customerId,
            customer_name: customerName,

            clientPositionId,
            ...({
              net_position: clientPosition?.net_position,
              long_qty: clientPosition?.long_qty,
              short_qty: clientPosition?.short_qty
            }),
            clientPosition
          });
        }
      });
    });

    Object.entries(allRows).forEach(([_key, rows]: any) => {
      rows.sort((a: ClientPositionTableRow, b: ClientPositionTableRow) => {
        const commodityCompare = a.commodity.localeCompare(b.commodity);
        if (commodityCompare !== 0) { return commodityCompare; }
        const customerCompare = a.customer_name.localeCompare(b.customer_name);
        if (customerCompare !== 0) { return customerCompare; }
        return a.clientPositionId.localeCompare(b.clientPositionId);
      })
    })

    const grouped_keys = getUniqueStrings(Object.keys(allRows).filter(item => allRows[item]?.length > 0)).sort();

    // Sort by commodity, then by customer name
    return {
      grouped_keys,
      all_rows: grouped_keys?.flatMap(item => allRows[item]) || [],
      hasRowValues
    };
  }, [data, clientPositions]);

  const loadingOrErrorUi = GenerateLoadingOrError([clientPositionListResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const columns: any[] = [
    {
      accessorKey: 'commodity',
      header: ({ table }: any) => (
        <>
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
          </button>{' '}
          Commodity
        </>
      ),
      cell: ({ row, getValue }: any) => (
        <div
          style={{
            // Since rows are flattened by default,
            // we can use the row.depth property
            // and paddingLeft to visually indicate the depth
            // of the row
            paddingLeft: `${row.depth * 2}rem`,
          }}
        >
          <div>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: 'pointer' },
                }}
              >
                {row.getIsExpanded() ? <ArrowDownIcon className="w-4 h-4" /> : <ArrowRightIcon className="w-4 h-4" />}
              </button>
            ) : (null)}{' '}
            {getValue()}
          </div>
        </div>
      ),
      footer: (props: any) => props.column.id,
    },
    {
      id: "customer_name",
      key: 'customer_name',
      name: 'Customer',
      className: "min-w-[300px]",
      cell: ({ row }: any) => row.original.customer_name,
      enableColumnFilter: true,
    },
    {
      id: "contract",
      key: 'contract',
      name: 'Contract',
      renderCell: ({ row }: any) => {
        return row.clientPosition.contract;
      },
      accessorFn: (row: any) => row.clientPosition?.contract || "",
      enableColumnFilter: true,
    },
    {
      key: 'exp_date',
      name: 'Exp Date',
      renderCell: ({ row }: any) => {
        return createDateLabel(getOptionPredictedExpirationDate(row.clientPosition?.option_month, row.commodity));
      }
    },
    {
      key: 'put_call_type',
      name: 'P/C',
      renderCell: ({ row }: any) => {
        return FlatPutOrCallOptions.find((item: { value: string }) => item.value === row.clientPosition.put_or_call)?.label || "";
      }
    },
    {
      key: 'strike_price',
      name: 'Strike',
      renderCell: ({ row }: any) => {
        return formatNumberToDisplay(row.clientPosition.strike_price, { emptyFormat: "--", });;
      }
    },
    {
      id: "net_position",
      key: 'net_position',
      name: 'Net',
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.clientPosition.net_position, { emptyFormat: "--", }); },
      aggregatedCell: ({ getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", }) },
    },
    {
      id: "long_qty",
      key: 'long_qty',
      name: 'Buy',
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.clientPosition.long_qty, { emptyFormat: "--", }); },
      aggregatedCell: ({ getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", }) },
    },
    {
      id: "short_qty",
      key: 'short_qty',
      name: 'Sell',
      aggregationFn: 'decimalAggregate',
      cell: ({ row }: any) => { return formatNumberToDisplay(row.original.clientPosition.short_qty, { emptyFormat: "--", }); },
      aggregatedCell: ({ getValue }: any) => { return formatNumberToDisplay(getValue(), { emptyFormat: "--", }) },
    },
    {
      key: 'avg_price',
      name: 'Avg Price',
      renderCell: ({ row }: any) => {
        return formatNumberToDisplay(row.clientPosition.premium, { emptyFormat: "--", });;
      }
    },
    {
      key: 'option_month',
      name: 'Option Month',
      renderCell: ({ row }: any) => {
        return row.clientPosition?.option_month;
      },
      cell: ({ row }: any) => row.original.clientPosition.option_month,
      enableColumnFilter: true,
    },
  ].map(item => ({ ...item, enableColumnFilter: item.enableColumnFilter || false, accessorKey: item.accessorKey || item.key, header: item.header || item.name, cell: item.cell || (item.renderCell ? ({ row }: any) => item.renderCell({ row: row.original }) : undefined) }));

  const toggleExpanded = (commodity: string) => {
    setExpanded(prev => ({
      ...prev,
      [commodity]: !prev[commodity]
    }));
  };

  return (
    <div className="space-y-2 p-2">
      <div className="">
        <div className="flex justify-between py-2">
          <h2 className="text-xl font-semibold mb-4">RJO</h2>
        </div>
        <div className="max-w-3/4 overflow-x-auto block">
          <DataTable
            defaultGrouping={["commodity"]}
            height="600px"
            columns={columns}
            data={all_rows!}
            className={``}
          />
        </div>
      </div>
    </div>
  );
}
